import React from "react";
import PropTypes from "prop-types";
import { LuCheck, LuChevronDown, LuMinus } from "react-icons/lu";
import "./formControls.css";

export { default as ThemedSelect } from "./ThemedSelect";

const sizeClass = (size) => `ir-control--${size.toLowerCase()}`;

export function FormField({
  label,
  required,
  hint,
  error,
  size,
  children,
  className = "",
}) {
  return (
    <label
      className={`ir-field ${sizeClass(size)}${
        error ? " ir-field--error" : ""
      } ${className}`.trim()}
    >
      {label && (
        <span className="ir-field__label">
          {label}
          {required && <span aria-hidden="true">*</span>}
        </span>
      )}
      {children}
      {(error || hint) && (
        <span className="ir-field__message">{error || hint}</span>
      )}
    </label>
  );
}

FormField.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
FormField.defaultProps = {
  required: false,
  hint: null,
  error: null,
  size: "md",
  label: null,
};

export function TextInput({
  label,
  required,
  hint,
  error,
  size,
  leadingIcon,
  trailingIcon,
  className = "",
  ...inputProps
}) {
  return (
    <FormField
      label={label}
      required={required}
      hint={hint}
      error={error}
      size={size}
      className={className}
    >
      <span className="ir-input-shell">
        {leadingIcon && (
          <span className="ir-input-shell__icon">{leadingIcon}</span>
        )}
        <input {...inputProps} aria-invalid={Boolean(error)} />
        {trailingIcon && (
          <span className="ir-input-shell__icon">{trailingIcon}</span>
        )}
      </span>
    </FormField>
  );
}

TextInput.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  leadingIcon: PropTypes.node,
  trailingIcon: PropTypes.node,
  className: PropTypes.string,
};
TextInput.defaultProps = {
  label: null,
  required: false,
  hint: null,
  error: null,
  size: "md",
  leadingIcon: null,
  trailingIcon: null,
};

export function SelectInput({
  label,
  required,
  hint,
  error,
  size,
  className = "",
  children,
  ...selectProps
}) {
  return (
    <FormField
      label={label}
      required={required}
      hint={hint}
      error={error}
      size={size}
      className={className}
    >
      <span className="ir-input-shell ir-select-shell">
        <select {...selectProps} aria-invalid={Boolean(error)}>
          {children}
        </select>
        <LuChevronDown aria-hidden="true" />
      </span>
    </FormField>
  );
}

SelectInput.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};
SelectInput.defaultProps = {
  label: null,
  required: false,
  hint: null,
  error: null,
  size: "md",
};

export function Checkbox({
  label,
  description,
  size,
  error,
  indeterminate,
  className = "",
  ...props
}) {
  return (
    <label
      className={`ir-choice ir-checkbox ${sizeClass(size)}${
        error ? " ir-choice--error" : ""
      }${
        indeterminate ? " ir-checkbox--indeterminate" : ""
      } ${className}`.trim()}
    >
      <input type="checkbox" {...props} aria-invalid={Boolean(error)} />
      <span className="ir-choice__control" aria-hidden="true">
        {indeterminate ? <LuMinus /> : <LuCheck />}
      </span>
      <span className="ir-choice__copy">
        <span>{label}</span>
        {description && <small>{description}</small>}
      </span>
    </label>
  );
}

Checkbox.propTypes = {
  label: PropTypes.node.isRequired,
  description: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  error: PropTypes.bool,
  indeterminate: PropTypes.bool,
  className: PropTypes.string,
};
Checkbox.defaultProps = {
  description: null,
  size: "md",
  error: false,
  indeterminate: false,
};

export function Radio({
  label,
  description,
  size,
  error,
  className = "",
  ...props
}) {
  return (
    <label
      className={`ir-choice ir-radio ${sizeClass(size)}${
        error ? " ir-choice--error" : ""
      } ${className}`.trim()}
    >
      <input type="radio" {...props} aria-invalid={Boolean(error)} />
      <span className="ir-choice__control" aria-hidden="true" />
      <span className="ir-choice__copy">
        <span>{label}</span>
        {description && <small>{description}</small>}
      </span>
    </label>
  );
}

Radio.propTypes = {
  label: PropTypes.node.isRequired,
  description: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  error: PropTypes.bool,
  className: PropTypes.string,
};
Radio.defaultProps = { description: null, size: "md", error: false };

export function Toggle({ label, description, size, className = "", ...props }) {
  return (
    <label
      className={`ir-choice ir-toggle ${sizeClass(size)} ${className}`.trim()}
    >
      <input type="checkbox" role="switch" {...props} />
      <span className="ir-toggle__track" aria-hidden="true">
        <span />
      </span>
      <span className="ir-choice__copy">
        <span>{label}</span>
        {description && <small>{description}</small>}
      </span>
    </label>
  );
}

Toggle.propTypes = {
  label: PropTypes.node.isRequired,
  description: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
};
Toggle.defaultProps = { description: null, size: "md" };
